import { AutocompleteValue } from "@mui/material";
import {
  ALGOLIA_INDICES,
  ALGOLIA_SEARCH_ZIP_CODES,
  EMPTY_ALGOLIA_QUERY,
} from "core/consts";
import type { AlgoliaAutocompleteOption, Validation } from "core/types";
import AlgoliaSelect, {
  AlgoliaSelectPresenterProps,
} from "dsl/atoms/AlgoliaSelect";
import { CSSProperties } from "react";
import { BasicDoc, Hit } from "react-instantsearch-core";

export type ZipcodeHit = Hit<
  BasicDoc & {
    __position: string;
    _geoloc: { lat: number; lon: number };
    city: string;
    code: string;
    federal_state: string;
  }
>;

function extractZipcodeFields(option: any) {
  const city = option.city || "";
  const code = "code" in option ? option.code : option.zipcode;
  const federal_state = option.federal_state || "";
  const zipcode = "zipcode" in option ? option.zipcode : option.code;

  return { city, code, federal_state, zipcode };
}

export function getAlgoliaZipcodeLabel(option: any) {
  const { city, code, federal_state } = extractZipcodeFields(option);

  return [code, city, federal_state].truthy().join(", ");
}

export function renderZipcodeOption(option: any) {
  const { city, code, federal_state, zipcode } = extractZipcodeFields(option);

  return (
    <span className="text-sm">
      {[
        `${code ?? zipcode}`,
        city,
        !city.includes(federal_state) && federal_state,
      ]
        .truthy()
        .join(", ")}
    </span>
  );
}

export function getHitRender() {
  return ({ _geoloc, city, code, federal_state }: ZipcodeHit) => {
    const label = getAlgoliaZipcodeLabel({
      city,
      code,
      federal_state,
    });

    return {
      id: code,
      label,
      latitude: _geoloc.lat,
      longitude: _geoloc.lon,
      value: code,
    };
  };
}

/** Option shape for ds/ui {@link ControlledAlgoliaAutocomplete} on zipcode indices. */
export type ZipcodeAlgoliaOption = AlgoliaAutocompleteOption & {
  latitude: number;
  longitude: number;
};

export function mapZipcodeHit(hit: ZipcodeHit): ZipcodeAlgoliaOption {
  const label = getAlgoliaZipcodeLabel({
    city: hit.city,
    code: hit.code,
    federal_state: hit.federal_state,
  });

  return {
    id: hit.code,
    label,
    latitude: hit._geoloc.lat,
    longitude: hit._geoloc.lon,
    value: hit.code,
  };
}

/**
 * Rehydrates a stored display string (or legacy full option) for RHF fields that keep
 * `profile._location` / `profile._destination` as plain strings.
 */
export function zipcodeOptionFromStoredValue(
  raw: unknown,
): ZipcodeAlgoliaOption | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === "string") {
    return {
      id: raw,
      label: raw,
      latitude: 0,
      longitude: 0,
      value: raw,
    };
  }
  if (
    typeof raw === "object" &&
    raw !== null &&
    "label" in raw &&
    typeof (raw as ZipcodeAlgoliaOption).label === "string"
  ) {
    const o = raw as ZipcodeAlgoliaOption;
    return {
      id: String(o.id ?? o.value ?? o.label),
      label: o.label,
      latitude: typeof o.latitude === "number" ? o.latitude : 0,
      longitude: typeof o.longitude === "number" ? o.longitude : 0,
      value: String(o.value ?? o.id ?? o.label),
    };
  }
  return null;
}

export type ZipcodeSelectValue = {
  id: any;
  label: string;
  latitude: number;
  longitude: number;
  value: any;
};

export type ZipcodesSelectProps<
  T extends ZipcodeSelectValue = ZipcodeSelectValue,
  Multiple extends boolean | undefined = undefined,
> = {
  allowedZipcodes?: string[];
  ariaLabel?: string;
  ariaLabelledBy?: string;
  country?: string;
  elementName: string;
  federalState?: string;
  hasErrorOverride?: boolean;
  id?: string;
  isConnected?: boolean;
  isMultiple?: Multiple;
  isRequired?: boolean;
  label?: string;
  ListboxProps?: AlgoliaSelectPresenterProps["ListboxProps"];
  noOptionsText?: string;
  onChange?: (value: AutocompleteValue<T, Multiple, false, false>) => void;
  pasteContext?: { buttonText: string; tooltipText: string };
  placeholder: string;
  validation?: Validation;
  value?: AutocompleteValue<T, Multiple, false, false>;
  withPaste?: boolean;
  withPortal?: boolean;
  zIndex?: CSSProperties["zIndex"];
};

export const getZipcodeFilters = ({
  allowedZipcodes,
  country,
  federalState,
}: {
  country?: string;
  federalState?: string;
  allowedZipcodes?: string[];
}) => {
  const filters = [];
  if (federalState) {
    filters.push(`federal_state:${federalState}`);
  }
  if (country) {
    filters.push(`country:${country}`);
  }
  if (allowedZipcodes?.length) {
    filters.push(`(code:${allowedZipcodes.join(" OR code:")})`);
  }
  return filters.join(" AND ");
};

export default function ZipcodesSelect<
  T extends ZipcodeSelectValue = ZipcodeSelectValue,
  Multiple extends boolean | undefined = undefined,
>({
  allowedZipcodes,
  ariaLabel,
  ariaLabelledBy,
  country,
  elementName,
  federalState,
  hasErrorOverride,
  id,
  isConnected,
  isMultiple,
  isRequired,
  label,
  ListboxProps,
  noOptionsText,
  onChange,
  pasteContext,
  placeholder,
  validation,
  value,
  withPaste,
  withPortal,
  zIndex,
}: ZipcodesSelectProps<T, Multiple>) {
  return (
    <AlgoliaSelect
      algoliaAnalyticsName={ALGOLIA_SEARCH_ZIP_CODES}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      defaultQuery={
        (value as any)?.value && typeof (value as any).value == "string"
          ? (value as any).value
          : EMPTY_ALGOLIA_QUERY
      }
      elementName={elementName}
      enableAutoHighlight={!isMultiple}
      filters={getZipcodeFilters({
        allowedZipcodes,
        country,
        federalState,
      })}
      hasErrorOverride={hasErrorOverride}
      id={id}
      indexName={ALGOLIA_INDICES.ZIPCODE}
      isConnected={isConnected}
      isMultiple={isMultiple}
      isRequired={isRequired}
      label={label}
      ListboxProps={ListboxProps}
      noOptionsText={noOptionsText}
      pasteContext={pasteContext}
      placeholder={placeholder}
      renderHits={getHitRender()}
      shouldCloseMenuOnSelect={!isMultiple}
      shouldFilterSelectedOptions={isMultiple}
      shouldSearchOnInitialRender
      shouldTruncateMenuItems
      validation={validation}
      value={value as any}
      withPaste={withPaste}
      withPortal={withPortal}
      withShortValue
      zIndex={zIndex}
      onChange={onChange}
    />
  );
}
